export type OpenedItem<TRow> = {
  row: TRow;
  /**
   * The row cannot be trusted to hold every member of the item, so the form stays the partial
   * view it is on a client's tab: shared fields and membership read-only, and no Delete.
   */
  isPartial: boolean;
};

/**
 * A client's tab reads only that client's rows, so a shared item reaches it as that member's
 * slice of the item. Opening the item has to show everyone on it — and deleting it has to reach
 * every member's copy — so the slice is traded for the row built from the family-wide read.
 *
 * Rows are keyed the way {@link groupCashflowItems} groups them, by group id and falling back to
 * the item's own id, so the same item is the same key in both reads. A row that has no
 * counterpart stays as it is, and is reported as partial: either the family read has not landed
 * or it dropped the members it could not read, and in both cases the slice is all there is.
 * `familyReadFailed` covers the subtler case — a counterpart built from a read that lost one
 * member's rows looks whole but names fewer members than share the item.
 */
export function wholeItem<TRow extends { id: string }>(
  row: TRow,
  wholeRows: TRow[],
  familyReadFailed = false,
): OpenedItem<TRow> {
  const whole = wholeRows.find(candidate => candidate.id === row.id);

  return { row: whole ?? row, isPartial: !whole || familyReadFailed };
}
